import { isNil } from 'ramda'
import moment from 'moment'

export const buildChatId = ({
  conversationId,
  receiverId,
  contextId,
  contextType
}) => {
  if (conversationId) {
    return String(conversationId)
  }

  const parts = [receiverId || 'unknown']

  if (!isNil(contextType)) {
    parts.push(String(contextType))
  } else {
    parts.push('personal')
  }

  if (!isNil(contextId)) {
    parts.push(String(contextId))
  }

  return parts.join('|')
}

export const ensureChatId = payload =>
  payload?.chatId ? payload.chatId : buildChatId(payload || {})

export const sortDistinct = list => {
  const seen = new Set()
  const result = []
  list.forEach(item => {
    if (item == null) return
    const key = String(item)
    if (seen.has(key)) return
    seen.add(key)
    result.push(key)
  })
  return result
}

export const pushDistinctFront = (list = [], value) => {
  if (value == null) return list.slice()
  const stringValue = String(value)
  const filtered = list.filter(item => String(item) !== stringValue)
  return [stringValue, ...filtered]
}

export const removeFromList = (list = [], value) => {
  if (value == null) return list.slice()
  const stringValue = String(value)
  return list.filter(item => String(item) !== stringValue)
}

export const nowTimestamp = () => Date.now()

const readMessageField = (message, field) => {
  if (!message) return null
  return typeof message.get === 'function' ? message.get(field) : message[field]
}

const flattenMessageGroups = groups =>
  (groups || []).reduce((messages, group) => {
    if (Array.isArray(group)) {
      messages.push(...group)
    }
    return messages
  }, [])

const messageHourKey = message => {
  const createdAt = moment(readMessageField(message, 'createdAt'))
  return createdAt.isValid()
    ? createdAt
        .local()
        .startOf('hour')
        .valueOf()
    : 'undated'
}

const messageFingerprint = message => {
  const rawCreatedAt = readMessageField(message, 'createdAt') || ''
  const timestamp = Date.parse(rawCreatedAt)
  const createdAt = Number.isNaN(timestamp)
    ? rawCreatedAt
    : Math.floor(timestamp / 1000)
  const text = readMessageField(message, 'text') || ''
  const type = readMessageField(message, 'isSystemMessage')
    ? 'system'
    : `user:${Boolean(readMessageField(message, 'isInterlocutorMessage'))}`

  return createdAt || text ? `${createdAt}|${text}|${type}` : null
}

const mergeDuplicateMessage = (current, incoming) => {
  const seenAt =
    readMessageField(incoming, 'seenAt') || readMessageField(current, 'seenAt')
  const readStateKnown =
    readMessageField(incoming, 'readStateKnown') === true ||
    readMessageField(current, 'readStateKnown') === true
  const clientId =
    readMessageField(incoming, 'clientId') ||
    readMessageField(current, 'clientId')
  const knownMessageIds =
    readMessageField(incoming, 'knownMessageIds') ||
    readMessageField(current, 'knownMessageIds')
  const knownMessageDate =
    readMessageField(incoming, 'knownMessageDate') ||
    readMessageField(current, 'knownMessageDate')
  const extras = {
    seenAt,
    readStateKnown,
    clientId,
    knownMessageIds,
    knownMessageDate
  }
  if (current && typeof current.merge === 'function') {
    return current.merge(incoming).merge(extras)
  }
  return { ...(current || {}), ...(incoming || {}), ...extras }
}

export const hasServerMessageId = message => {
  const id = readMessageField(message, 'id')
  return id != null && !String(id).startsWith('client:')
}

export const isUnreadIncomingMessage = message =>
  Boolean(
    readMessageField(message, 'isInterlocutorMessage') &&
      !readMessageField(message, 'isSystemMessage') &&
      readMessageField(message, 'readStateKnown') === true &&
      !readMessageField(message, 'seenAt')
  )

const reconcileLocalMessages = messages => {
  const serverMessages = new Map()
  const localMessages = new Map()
  messages.forEach((message, index) => {
    const serverId =
      hasServerMessageId(message) && String(readMessageField(message, 'id'))
    const key =
      serverId || readMessageField(message, 'clientId') || `legacy:${index}`
    const target = serverId ? serverMessages : localMessages
    target.set(
      key,
      target.has(key)
        ? mergeDuplicateMessage(target.get(key), message)
        : message
    )
  })
  const claimedIds = new Set()
  const remainingLocal = []
  localMessages.forEach(local => {
    const clientId = readMessageField(local, 'clientId')
    const eligible =
      !readMessageField(local, 'isInterlocutorMessage') &&
      !readMessageField(local, 'isSystemMessage') &&
      (Boolean(clientId) || readMessageField(local, 'sendStatus') === 'sent')
    const baseline = new Set(readMessageField(local, 'knownMessageIds') || [])
    const localTime = Date.parse(readMessageField(local, 'createdAt'))
    const knownServerTime = Date.parse(
      readMessageField(local, 'knownMessageDate')
    )
    const match =
      eligible &&
      Array.from(serverMessages.entries()).find(([id, server]) => {
        const serverClientId = readMessageField(server, 'clientId')
        if (clientId && serverClientId === clientId) return true
        if (claimedIds.has(id) || serverClientId || baseline.has(id)) {
          return false
        }
        if (
          readMessageField(server, 'isInterlocutorMessage') ||
          readMessageField(server, 'isSystemMessage')
        ) {
          return false
        }
        if (
          readMessageField(local, 'text') !== readMessageField(server, 'text')
        ) {
          return false
        }
        const serverTime = Date.parse(readMessageField(server, 'createdAt'))
        // A temporary bubble may only match a new server record, never an old
        // occurrence of the same text. Distinct server IDs are always retained.
        if (!Number.isFinite(serverTime)) return false
        if (clientId && Number.isFinite(knownServerTime)) {
          return serverTime >= knownServerTime
        }
        if (
          clientId &&
          Array.isArray(readMessageField(local, 'knownMessageIds')) &&
          !baseline.size
        ) {
          return true
        }
        return (
          Number.isFinite(localTime) && Math.abs(serverTime - localTime) <= 2000
        )
      })
    if (match) {
      const [id, server] = match
      claimedIds.add(id)
      const metadata = {
        clientId: clientId || readMessageField(server, 'clientId'),
        knownMessageIds: null,
        knownMessageDate: null
      }
      serverMessages.set(
        id,
        typeof server.merge === 'function'
          ? server.merge(metadata)
          : { ...server, ...metadata }
      )
    } else {
      remainingLocal.push(local)
    }
  })
  return [...serverMessages.values(), ...remainingLocal]
}

export const mergeMessageGroups = (...collections) => {
  const messages = []
  const idIndexes = new Map()
  const fingerprintIndexes = new Map()

  const reconciled = reconcileLocalMessages(
    collections.reduce(
      (all, groups) => all.concat(flattenMessageGroups(groups)),
      []
    )
  )
  reconciled.forEach(message => {
    const id = readMessageField(message, 'id')
    const fingerprint = messageFingerprint(message)
    const idIndex = id != null ? idIndexes.get(String(id)) : undefined
    const fingerprintIndex = fingerprint
      ? fingerprintIndexes.get(fingerprint)
      : undefined
    let existingIndex = idIndex
    if (
      existingIndex === undefined &&
      fingerprintIndex !== undefined &&
      (id == null || readMessageField(messages[fingerprintIndex], 'id') == null)
    ) {
      existingIndex = fingerprintIndex
    }

    if (existingIndex != null) {
      messages[existingIndex] = mergeDuplicateMessage(
        messages[existingIndex],
        message
      )
      if (id != null) idIndexes.set(String(id), existingIndex)
      if (fingerprint) fingerprintIndexes.set(fingerprint, existingIndex)
      return
    }

    const nextIndex = messages.length
    messages.push(message)
    if (id != null) idIndexes.set(String(id), nextIndex)
    if (fingerprint) fingerprintIndexes.set(fingerprint, nextIndex)
  })

  const sorted = messages
    .map((message, index) => ({ message, index }))
    .sort((left, right) => {
      const leftTime = Date.parse(readMessageField(left.message, 'createdAt'))
      const rightTime = Date.parse(readMessageField(right.message, 'createdAt'))
      const normalizedLeftTime = Number.isNaN(leftTime) ? Infinity : leftTime
      const normalizedRightTime = Number.isNaN(rightTime) ? Infinity : rightTime
      return (
        normalizedLeftTime - normalizedRightTime || left.index - right.index
      )
    })
    .map(({ message }) => message)

  return sorted.reduce((groups, message) => {
    const groupKey = messageHourKey(message)
    const lastGroup = groups[groups.length - 1]
    const lastMessage = lastGroup?.[0]
    const lastGroupKey = lastGroup ? messageHourKey(lastMessage) : null

    if (lastGroup && lastGroupKey === groupKey) {
      lastGroup.push(message)
    } else {
      groups.push([message])
    }
    return groups
  }, [])
}
